import { ComponentProps } from 'react';

import Figure from '../../../../../components/Figure/Figure';
import Inline from '../../../../../components/Inline/Inline';
import InlineIcon from '../../../../../components/InlineIcon/InlineIcon';
import PaddedBox from '../../../../../components/PaddedBox/PaddedBox';
import Paragraph from '../../../../../components/Paragraph/Paragraph';
import Stack from '../../../../../components/Stack/Stack';
import Title from '../../../../../components/Title/Title';
import BoatModelWithMedia from '../../../../types/BoatModelWithMedia';
import DockWithMedia from '../../../../types/DockWithMedia';
import boatModelPlaceholder from '../../graphics/boatModelPlaceholder.svg';
import dockPlaceholder from '../../graphics/dockPlaceholder.svg';
import passengersIcon from '../../graphics/passengersIcon.svg?raw';
import NumberOfBoatsBadge from '../NumberOfBoatsBadge/NumberOfBoatsBadge';

type Parameters = {
    dock?: DockWithMedia;
    boatModel?: BoatModelWithMedia;
    numberOfPassengers?: number;
    numberOfBoats?: number;
};

const determineImageAndTitle = (parameters: Parameters) => {
    if (parameters.boatModel) {
        return {
            thumb:
                parameters.boatModel.media[0]?.variants.thumb.path ??
                boatModelPlaceholder,
            title: parameters.boatModel.name,
        };
    }

    if (parameters.dock) {
        return {
            thumb:
                parameters.dock.media[0]?.variants.thumb.path ??
                dockPlaceholder,
            title: parameters.dock.name,
        };
    }

    return {
        thumb: boatModelPlaceholder,
        title: null,
    };
};

type BookingParametersHeroProps = {
    parameters: Parameters;
    onlyTitle?: boolean;
    compact?: boolean;
    editMode?: boolean;
    borderRadiusSize?: ComponentProps<typeof Figure>['borderRadiusSize'];
};

const BookingParametersHero = ({
    parameters,
    borderRadiusSize,
    onlyTitle = false,
    compact = false,
    editMode = false,
}: BookingParametersHeroProps) => {
    const { dock, boatModel, numberOfPassengers, numberOfBoats } = parameters;

    if (!dock && !boatModel) {
        return null;
    }

    const { title, thumb } = determineImageAndTitle(parameters);

    return (
        <Figure
            borderRadiusSize={borderRadiusSize}
            borderRadius="top"
            src={thumb}
            defaultPlaceholderRatio={1 / 2}
            vignette={thumb !== boatModelPlaceholder ? 'bottomLeft' : 'none'}
            captionPosition="inside-no-padding"
            caption={
                <PaddedBox
                    horizontalPadding={compact ? 'small' : 'large'}
                    verticalPadding={compact ? 'small' : 'large'}
                >
                    <Stack space="xsmall">
                        {boatModel && (
                            <Inline vertical="middle">
                                <Title
                                    variant={onlyTitle ? 'area' : 'section'}
                                    lineHeight="small"
                                >
                                    {title}
                                </Title>

                                {numberOfBoats && numberOfBoats > 1 && (
                                    <NumberOfBoatsBadge
                                        numberOfBoats={numberOfBoats}
                                        editMode={editMode}
                                    />
                                )}
                            </Inline>
                        )}

                        {!onlyTitle && (
                            <Inline>
                                {boatModel && dock && (
                                    <Paragraph size="small">
                                        {dock.name}
                                    </Paragraph>
                                )}

                                {numberOfPassengers && (
                                    <Inline space="small">
                                        <InlineIcon src={passengersIcon} />
                                        {numberOfPassengers}
                                    </Inline>
                                )}
                            </Inline>
                        )}
                    </Stack>
                </PaddedBox>
            }
        />
    );
};

export default BookingParametersHero;
